import { useState } from 'react';

interface NavigationProps {
  isScrolled: boolean;
}

export default function Navigation({ isScrolled }: NavigationProps) {
  const [showModal, setShowModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: '홈', id: 'home' },
    { label: '소개', id: 'about' },
    { label: '서비스', id: 'services' },
    { label: '오시는 길', id: 'location' },
    { label: 'FAQ', id: 'faq' },
    { label: '문의', id: 'contact' },
  ];

  return (
    <>
      {/* 전화/문자 선택 모달 */}
      {showModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="rounded-2xl p-6 w-72 flex flex-col gap-4"
            style={{ background: '#1a1a1a', border: '1px solid rgba(236,72,153,0.4)', boxShadow: '0 0 40px rgba(236,72,153,0.3)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-center text-white font-semibold text-lg tracking-wide">예약 방법 선택</p>
            <a
              href="tel:01057815340"
              className="flex items-center justify-center gap-3 py-4 rounded-full font-semibold text-black transition-all whitespace-nowrap cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #ec4899, #f97316)', boxShadow: '0 0 20px rgba(236,72,153,0.5)' }}
              onClick={() => setShowModal(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-lg"></i></span>
              전화 통화
            </a>
            <a
              href="sms:01057815340"
              className="flex items-center justify-center gap-3 py-4 rounded-full font-semibold text-pink-400 border border-pink-500 hover:bg-pink-500 hover:text-black transition-all whitespace-nowrap cursor-pointer"
              style={{ boxShadow: '0 0 15px rgba(236,72,153,0.3)' }}
              onClick={() => setShowModal(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-message-2-fill text-lg"></i></span>
              문자 메시지
            </a>
            <button
              className="text-gray-500 text-sm mt-1 hover:text-gray-300 transition-colors cursor-pointer"
              onClick={() => setShowModal(false)}
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {/* 모바일 드로어 메뉴 */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[90] flex"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="ml-auto w-72 h-full flex flex-col py-20 px-8 gap-6"
            style={{ background: 'rgba(10,10,10,0.98)', borderLeft: '1px solid rgba(236,72,153,0.3)', boxShadow: '-10px 0 40px rgba(236,72,153,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-left text-lg font-light text-gray-300 hover:text-pink-400 transition-colors cursor-pointer py-2 border-b border-gray-800 whitespace-nowrap"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => { setMobileMenuOpen(false); setShowModal(true); }}
              className="mt-4 px-6 py-3 rounded-full font-semibold text-black transition-all whitespace-nowrap cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #ec4899, #f97316)', boxShadow: '0 0 20px rgba(236,72,153,0.5)' }}
            >
              예약하기
            </button>
          </div>
        </div>
      )}

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/95 backdrop-blur-md shadow-[0_0_20px_rgba(236,72,153,0.2)]' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* 로고 */}
            <span className="text-xl md:text-2xl font-bold tracking-widest" style={{ fontFamily: "'Orbitron', sans-serif", color: '#fff', textShadow: '0 0 10px #ec4899, 0 0 20px #ec4899' }}>
              BUSAN<span style={{ color: '#ec4899' }}>HOST</span>
            </span>

            {/* 데스크탑 메뉴 */}
            <div className="hidden md:flex items-center gap-10">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm font-light transition-all whitespace-nowrap cursor-pointer text-gray-300 hover:text-pink-400"
                  style={{ textShadow: 'none' }}
                  onMouseEnter={e => (e.currentTarget.style.textShadow = '0 0 8px #ec4899')}
                  onMouseLeave={e => (e.currentTarget.style.textShadow = 'none')}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* 데스크탑 예약 버튼 */}
            <button
              onClick={() => setShowModal(true)}
              className="hidden md:block px-6 py-2 rounded-full border border-pink-500 text-pink-400 hover:bg-pink-500 hover:text-black transition-all whitespace-nowrap cursor-pointer font-medium"
              style={{ boxShadow: '0 0 10px rgba(236,72,153,0.4)' }}
            >
              예약하기
            </button>

            {/* 모바일 햄버거 버튼 */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-300 hover:text-pink-400 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(true)}
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}
